/**
 * ConvexErrorBoundary — notifications-scoped React error boundary.
 *
 * Catches Convex platform errors (network drops, server crashes, malformed
 * query identifier exceptions) that propagate through React's error-boundary
 * mechanism from `useQuery`. Renders `<ErrorState>` with a Retry button that
 * clears the error and re-renders children.
 *
 * Emits a `notifications.stream.error` structured log on every capture
 * (specs/prd.md Section 11.3).
 *
 * This is a 30-50 line class component per FR-008.
 *
 * @see specs/prd.md Section 3 FR-008
 */
import React from "react";
import { ErrorState } from "../cross-cutting/ErrorState";

interface ConvexErrorBoundaryProps {
  children: React.ReactNode;
}

interface ConvexErrorBoundaryState {
  hasError: boolean;
}

export class ConvexErrorBoundary extends React.Component<
  ConvexErrorBoundaryProps,
  ConvexErrorBoundaryState
> {
  constructor(props: ConvexErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ConvexErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    try {
      console.error(
        JSON.stringify({
          event: "notifications.stream.error",
          level: "ERROR",
          error_message: error.message,
          component_stack: errorInfo.componentStack ?? null,
          ts_ms: Date.now(),
        }),
      );
    } catch {
      // Logging failure must not crash the boundary.
    }
  }

  private readonly resetBoundary = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorState
          message="Couldn't load notifications. Try again."
          onRetry={this.resetBoundary}
        />
      );
    }
    return this.props.children;
  }
}
